import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { useSelectedFacility } from "@/app/FacilityContext";
import { useSelectedOrganization } from "@/app/OrganizationContext";
import { useMyOrg } from "@/hooks/use-my-org";
import { listMyOrgOptions } from "@/libs/onboardingApi";

export function OrganizationSelector() {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const restoreFocusRef = useRef(false);
  const restoreFocusFrameRef = useRef<number | null>(null);
  const view = useMyOrg();
  const { organizationId, selectOrganization } = useSelectedOrganization();
  const { clearFacility } = useSelectedFacility();
  const { data: organizations } = useQuery({
    queryKey: ["org", "me", "options"],
    queryFn: listMyOrgOptions,
    staleTime: 60_000,
  });
  const open = Boolean(anchorEl);
  const selectedOrganization =
    organizations?.find(
      (organization) => organization.org_id === organizationId,
    ) ?? view.org;

  useEffect(
    () => () => {
      if (restoreFocusFrameRef.current !== null) {
        cancelAnimationFrame(restoreFocusFrameRef.current);
      }
    },
    [],
  );

  if (!selectedOrganization?.org_id) {
    return null;
  }

  // A single organization needs no switcher in the top bar; its name is in
  // the account menu.
  if (!organizations || organizations.length <= 1) {
    return null;
  }

  const closeMenu = (restoreFocus = false) => {
    restoreFocusRef.current = restoreFocus;
    setAnchorEl(null);
  };

  return (
    <>
      <ButtonBase
        ref={triggerRef}
        type="button"
        aria-label={`Organization selector. Current organization: ${selectedOrganization.business_name ?? "none"}`}
        title={selectedOrganization.business_name ?? undefined}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(event) =>
          setAnchorEl((current) => (current ? null : event.currentTarget))
        }
        sx={{
          display: "flex",
          minWidth: 0,
          maxWidth: { xs: 140, md: 200 },
          minHeight: 32,
          alignItems: "center",
          gap: 0.5,
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          pl: 0.75,
          pr: 0.5,
          py: 0.25,
          textAlign: "left",
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <ApartmentOutlinedIcon
          fontSize="small"
          aria-hidden="true"
          sx={{ color: "text.secondary" }}
        />
        <Typography
          component="span"
          variant="body2"
          noWrap
          sx={{ minWidth: 0, lineHeight: 1.25 }}
        >
          {selectedOrganization.business_name ?? "Select organization"}
        </Typography>
        <ExpandMoreRoundedIcon
          fontSize="small"
          color="action"
          aria-hidden="true"
        />
      </ButtonBase>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => closeMenu()}
        slotProps={{
          paper: {
            sx: {
              minWidth: 248,
              maxWidth: 360,
            },
          },
          list: { "aria-label": "Organizations" },
          transition: {
            onExited: () => {
              if (!restoreFocusRef.current) return;
              restoreFocusRef.current = false;
              // Modal performs its own focus cleanup after this callback. Move
              // the explicit restoration to the next frame so an organization-
              // context rerender cannot leave focus on the document body.
              restoreFocusFrameRef.current = requestAnimationFrame(() => {
                restoreFocusFrameRef.current = null;
                triggerRef.current?.focus();
              });
            },
          },
        }}
      >
        {organizations.map((organization) => {
          const selected = organization.org_id === selectedOrganization.org_id;
          return (
            <MenuItem
              key={organization.org_id}
              selected={selected}
              aria-current={selected ? "true" : undefined}
              onClick={() => {
                clearFacility();
                selectOrganization(organization.org_id);
                closeMenu(true);
              }}
              sx={{ alignItems: "flex-start", whiteSpace: "normal" }}
            >
              <ListItemText
                primary={organization.business_name}
                slotProps={{
                  primary: {
                    variant: "body2",
                    sx: {
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                      lineHeight: 1.35,
                    },
                  },
                }}
              />
              {selected ? (
                <ListItemIcon sx={{ minWidth: 28, justifyContent: "flex-end" }}>
                  <CheckRoundedIcon fontSize="small" aria-hidden="true" />
                </ListItemIcon>
              ) : (
                <Box sx={{ width: 28 }} />
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
